import * as React from 'react';
import { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import HTag from './HTag';
import DocumentMenu from './Document/DocumentMenu';
import {
    DataStore,
    EntityGroupStore,
    EntityStore,
    SuperscriptScope,
} from '../stores';
import { ShareClassDocument } from '../models/entity';
import { Theme } from '../models/Theme';

const DocumentWrapper = styled<any, any>('li', 'documentWrapper', 'document')`
    ${propStyles};
    position: relative;
    list-style: none;
`;

DocumentWrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    flex: ['none', '0 0 50%'],
};

const DocumentType = styled<any, any>('div', 'documentType', 'document')`
    ${propStyles};
`;

DocumentType.defaultProps = {
    display: 'flex',
    flex: '1 0 auto',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginX: 'sm',
    marginY: 'xs',
    paddingY: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
};

const DocumentSelector = styled<any, any>(
    'div',
    'documentSelector',
    'document'
)`
    ${propStyles};
`;

DocumentSelector.defaultProps = {
    display: 'flex',
    margin: 'auto 0',
};

interface DocumentProps {
    theme: Theme;
    dataStore?: DataStore;
    entityStore?: EntityStore;
    entityGroupStore?: EntityGroupStore;
    groupedDocuments: {};
    documentGroupName?: string;
    documents: Array<ShareClassDocument>;
    metricContext: string;
    superscriptScope?: SuperscriptScope;
    showSingleDocument?: boolean;
    describedBy?: string;
}

@inject('dataStore', 'entityStore', 'entityGroupStore')
@observer
class Document extends Component<DocumentProps, any> {
    @computed
    get documentCount() {
        return this.props.documents.length;
    }

    @computed
    get selectedCount() {
        const { documentSelected } = this.props.entityStore;
        return this.props.documents.filter(document =>
            documentSelected(document.clientCode)
        ).length;
    }

    @computed
    get title() {
        const { documentGroupName } = this.props;
        if (
            documentGroupName &&
            this.documentTypeAliases &&
            this.documentTypeAliases[documentGroupName]
        ) {
            return this.documentTypeAliases[documentGroupName];
        }
        return documentGroupName;
    }

    @computed
    get documentTypeAliases() {
        const { rowConfig, globalConfig } = this.props.dataStore.config;
        if (rowConfig && rowConfig.documentTypeAliases) {
            return rowConfig.documentTypeAliases;
        }
        if (
            globalConfig &&
            globalConfig.valueAliases &&
            globalConfig.valueAliases.documentTypes
        ) {
            return globalConfig.valueAliases.documentTypes;
        }
        return;
    }

    @computed
    get selectedLabel() {
        return this.selectedCount === 1
            ? this.selectedDocumentsLabel.singular
            : this.selectedDocumentsLabel.plural;
    }

    @computed
    get availableLabel() {
        return this.documentCount === 1
            ? this.availableDocumentsLabel.singular
            : this.availableDocumentsLabel.plural;
    }

    @computed
    get selectedDocumentsLabel() {
        const { selectedDocumentsLabel } = this.props.dataStore.config;
        return selectedDocumentsLabel;
    }

    @computed
    get availableDocumentsLabel() {
        const { availableDocumentsLabel } = this.props.dataStore.config;
        return availableDocumentsLabel;
    }

    render() {
        const { selectedCount, documentCount } = this;
        const {
            theme,
            dataStore,
            entityStore,
            entityGroupStore,
            documents,
            metricContext,
            superscriptScope,
            showSingleDocument,
            describedBy,
        } = this.props;

        const { isMobile } = dataStore;

        return (
            <DocumentWrapper>
                <DocumentType {...theme.DocumentType}>
                    <HTag
                        componentTheme={
                            isMobile ? 'DocumentTitleMobile' : 'DocumentTitle'
                        }
                        defaultProps={{
                            flex: '1 0 auto',
                            fontSize: 'inherit',
                            fontWeight: 'normal',
                            margin: '0 1em 0 0',
                        }}
                        level={3}
                        text={this.title}
                    />
                    <DocumentSelector
                        {...(isMobile
                            ? theme.DocumentSelectorMobile
                            : theme.DocumentSelector)}
                    >
                        <DocumentMenu
                            documents={documents}
                            selected={entityStore.documentSelected}
                            onChange={entityGroupStore.toggleDownload}
                            showCount={false}
                            showCheckbox={true}
                            showSingleDocument={showSingleDocument}
                            showSingleDocumentLabel={true}
                            theme={{
                                ...theme,
                                ...{
                                    CheckboxLabel: {
                                        minWidth: '0',
                                    },
                                    DownloadButton:
                                        theme.DocumentCheckboxDownloadButton,
                                    DownloadCount:
                                        theme.DocumentCheckboxDownloadCount,
                                    DocumentListItem: {
                                        display: 'flex',
                                    },
                                },
                            }}
                            metricContext={`document-${metricContext}`}
                            superscriptScope={superscriptScope}
                            describedBy={describedBy}
                        >
                            {selectedCount > 0 ? (
                                <Translate
                                    placeholders={{
                                        selected: selectedCount,
                                        total: documentCount,
                                    }}
                                >
                                    {this.selectedLabel}
                                </Translate>
                            ) : (
                                <Translate
                                    placeholders={{ total: documentCount }}
                                >
                                    {this.availableLabel}
                                </Translate>
                            )}
                        </DocumentMenu>
                    </DocumentSelector>
                </DocumentType>
            </DocumentWrapper>
        );
    }
}

export default withTheme(Document) as any;



// WEBPACK FOOTER //
// ./src/Components/Document.tsx